import { DeleteOutlined, EditOutlined, PlusOutlined, PlayCircleOutlined, HistoryOutlined, PoweroffOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Col, Drawer, Form, Modal, Pagination, Row, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cancelProducerRun, deleteProducerSchedules, getProducerScheduleRunSummary, listProducerSchedules, triggerProducerScheduleOnce, updateProducerSchedule } from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';
import ProducerScheduleEditorModal from './ProducerScheduleEditorModal';
import ProducerRunViewer from './ProducerRunViewer';
import ProduceRunHistoryDrawer from './ProduceRunHistoryDrawer';

const { Text } = Typography;

const PRODUCER_OPTIONS = [
  { label: '论文→小红书笔记', value: 'papers_to_notes' },
  { label: '论文→短视频', value: 'papers_to_video' },
  { label: '论文→公众号文章', value: 'papers_to_wechat_article' },
  { label: '小红书抓取→视频导入', value: 'xhs_video_importer' },
  { label: '视频双语转换', value: 'video_bilingual_convert' },
];

const PRODUCER_LABEL_MAP = {
  ...Object.fromEntries(PRODUCER_OPTIONS.map((opt) => [opt.value, opt.label])),
  weixin_channels_video_importer: '视频号视频导入',
};

const TRIGGER_TYPE_LABEL = {
  cron: '定时表达式（Cron）',
  interval: '间隔',
  fixed_time: '一次',
  fixed: '一次',
  once: '一次',
};

const joinList = (value, sep = ', ') => {
  if (!value) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(sep);
  return String(value);
};

const renderWithTooltip = (text, maxLen = 32) => {
  const str = text ? String(text) : '';
  if (!str) return <span>-</span>;
  if (str.length <= maxLen) return <span>{str}</span>;
  return (
    <Tooltip title={<div style={{ maxWidth: 560, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{str}</div>}>
      <span>{str.slice(0, maxLen)}…</span>
    </Tooltip>
  );
};

const formatScheduleSummary = (schedule) => {
  if (!schedule) return '-';
  const triggerType = String(schedule.trigger_type || 'cron');
  const tz = schedule.timezone ? ` (${schedule.timezone})` : '';
  if (triggerType === 'cron') return `${schedule.cron_expression || '-'}${tz}`;
  if (triggerType === 'interval') return `每 ${schedule.interval_seconds || '-'} 秒${tz}`;
  if (triggerType === 'fixed_time' || triggerType === 'fixed' || triggerType === 'once') return `一次：${schedule.run_at || '-'}${tz}`;
  return JSON.stringify(schedule);
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-';
  const raw = String(dateTimeString);
  const normalized = raw.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, '$1$2');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const ProducerSchedulesPanel = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const paginationRef = useRef(pagination);
  const recentlyDeletedScheduleIdsRef = useRef(new Map());
  const [filterProducer, setFilterProducer] = useState(undefined);
  const [filterEnabled, setFilterEnabled] = useState(undefined);
  const [runSummaryByScheduleId, setRunSummaryByScheduleId] = useState({});
  const [runViewerOpen, setRunViewerOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState('');
  const [activeProducerName, setActiveProducerName] = useState('papers_to_notes');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySchedule, setHistorySchedule] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const refresh = useCallback(async (page = null, pageSize = null) => {
    setLoading(true);
    try {
      const current = page ?? paginationRef.current.current;
      const size = pageSize ?? paginationRef.current.pageSize;
      const resp = await listProducerSchedules({
        page: current,
        page_size: size,
        producer_name: filterProducer || undefined,
        enabled: filterEnabled,
      });
      if (resp?.status === 'success') {
        const ttlMs = 8000;
        const now = Date.now();
        for (const [id, ts] of recentlyDeletedScheduleIdsRef.current.entries()) {
          if (!ts || now - ts > ttlMs) recentlyDeletedScheduleIdsRef.current.delete(id);
        }
        const list = Array.isArray(resp.items) ? resp.items : [];
        const filtered = recentlyDeletedScheduleIdsRef.current.size > 0
          ? list.filter((it) => !recentlyDeletedScheduleIdsRef.current.has(it?.schedule_id))
          : list;
        setItems(filtered);
        try {
          const scheduleIds = filtered.map((it) => it?.schedule_id).filter(Boolean);
          const ar = await getProducerScheduleRunSummary({ schedule_ids: scheduleIds });
          if (ar?.status === 'success' && ar?.items && typeof ar.items === 'object') {
            setRunSummaryByScheduleId(ar.items);
          } else {
            setRunSummaryByScheduleId({});
          }
        } catch (e) {
          setRunSummaryByScheduleId({});
        }
        setPagination({
          current: resp.page || current,
          pageSize: resp.page_size || size,
          total: resp.total || 0,
        });
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '获取任务列表失败'));
      }
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [filterEnabled, filterProducer]);

  useEffect(() => {
    refresh(1, paginationRef.current.pageSize);
  }, [refresh]);

  const markScheduleIdsDeleted = useCallback((scheduleIds = []) => {
    const now = Date.now();
    const ids = Array.from(new Set((scheduleIds || []).filter(Boolean)));
    ids.forEach((id) => recentlyDeletedScheduleIdsRef.current.set(id, now));
  }, []);

  const reloadAfterDelete = useCallback(async (deletedScheduleIds = []) => {
    const ids = Array.from(new Set((deletedScheduleIds || []).filter(Boolean)));
    const currentPageIds = (items || []).map((it) => it?.schedule_id).filter(Boolean);
    const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
    const shouldBackPage = paginationRef.current.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
    const nextPage = shouldBackPage ? paginationRef.current.current - 1 : paginationRef.current.current;
    await refresh(nextPage, paginationRef.current.pageSize);
  }, [items, refresh]);

  const columns = useMemo(() => ([
    {
      title: '任务ID',
      dataIndex: 'schedule_id',
      key: 'schedule_id',
      width: 180,
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 220,
      render: (v) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
          {v || '-'}
        </div>
      ),
    },
    {
      title: '生产能力',
      dataIndex: 'producer_name',
      key: 'producer_name',
      width: 170,
      render: (v) => <Text>{PRODUCER_LABEL_MAP[v] || v || '-'}</Text>,
    },
    {
      title: '触发方式',
      dataIndex: 'schedule',
      key: 'trigger_type',
      width: 110,
      render: (schedule) => {
        const key = schedule?.trigger_type ? String(schedule.trigger_type) : '';
        return <Text>{TRIGGER_TYPE_LABEL[key] || key || '-'}</Text>;
      },
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (v) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
    },
    {
      title: '运行中',
      key: 'active_run',
      width: 90,
      render: (_, row) => {
        const active = runSummaryByScheduleId?.[row?.schedule_id]?.active_run;
        return active ? <Tag color="gold">运行中</Tag> : <Tag>-</Tag>;
      },
    },
    {
      title: '最近运行',
      key: 'latest_run',
      width: 220,
      render: (_, row) => {
        const latest = runSummaryByScheduleId?.[row?.schedule_id]?.latest_run;
        if (!latest) return <Text>-</Text>;
        const st = String(latest.status || '');
        const color = st === 'succeeded' ? 'green' : st === 'failed' ? 'red' : st === 'running' ? 'gold' : 'default';
        return (
          <Space direction="vertical" size={0}>
            <Tag color={color} style={{ marginRight: 0 }}>{st || '-'}</Tag>
            <Text style={{ fontSize: 12, color: '#666' }}>{formatDateTime(latest.updated_at || latest.created_at)}</Text>
          </Space>
        );
      },
    },
    {
      title: '关键词',
      dataIndex: 'params',
      key: 'keywords',
      width: 240,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (params) => {
        const text = joinList(params?.keywords || []);
        if (!text) return <span>-</span>;
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '创建者',
      dataIndex: 'params',
      key: 'creator_user_id',
      width: 120,
      render: (params) => <Text>{params?.creator_user_id || '-'}</Text>,
    },
    {
      title: '目标平台',
      dataIndex: 'params',
      key: 'available_platforms',
      width: 180,
      render: (params) => renderWithTooltip(joinList(params?.available_platforms || []), 28),
    },
    {
      title: '定时规则',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 260,
      render: (v) => <Text>{formatScheduleSummary(v)}</Text>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 190,
      render: (v) => <Text>{formatDateTime(v)}</Text>,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (v) => <Text>{formatDateTime(v)}</Text>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 420,
      fixed: 'right',
      render: (_, row) => {
        const activeRun = runSummaryByScheduleId?.[row?.schedule_id]?.active_run;
        const activeRunId = activeRun?.run_id;
        return (
          <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => {
              if (!row?.enabled) {
                message.warning('任务未启用，不能立即执行');
                return;
              }
              if (runSummaryByScheduleId?.[row?.schedule_id]?.active_run) {
                message.warning('任务正在运行，已阻止本次触发');
                return;
              }

              Modal.confirm({
                title: '立即执行',
                content: '将立即执行一次该生产任务（不改变原定时规则）。确定要执行吗？',
                okText: '立即执行',
                cancelText: '取消',
                onOk: async () => {
                  try {
                    const resp = await triggerProducerScheduleOnce({ schedule_id: row.schedule_id });
                    if (resp?.status === 'accepted') {
                      message.success('已触发一次运行');
                      setActiveRunId(resp.run_id);
                      setActiveProducerName(row.producer_name);
                      setRunViewerOpen(true);
                      refresh();
                      return;
                    }
                    if (resp?.status === 'conflict') {
                      message.warning('任务正在运行，已阻止本次触发');
                      refresh();
                      return;
                    }
                    if (resp?.status === 'forbidden') {
                      message.warning('任务未启用，不能立即执行');
                      refresh();
                      return;
                    }
                    message.error(resolveErrorMessage(resp?.error_message, '触发失败'));
                    refresh();
                  } catch (e) {
                    message.error(e?.message || String(e));
                    refresh();
                  }
                },
              });
            }}
            disabled={!row?.enabled || !!runSummaryByScheduleId?.[row?.schedule_id]?.active_run}
            style={{ padding: '0 4px' }}
          >
            立即执行
          </Button>

          <Button
            type="link"
            size="small"
            icon={<StopOutlined />}
            onClick={() => {
              if (!activeRunId) {
                message.warning('当前没有运行中的实例');
                return;
              }
              Modal.confirm({
                title: '停止运行',
                content: '将停止当前正在运行的这一次生产（可能需要一点时间生效）。确定要停止吗？',
                okText: '停止运行',
                cancelText: '取消',
                onOk: async () => {
                  try {
                    const resp = await cancelProducerRun(activeRunId);
                    if (resp?.status === 'accepted') {
                      message.success('已提交停止请求');
                      refresh();
                      return;
                    }
                    message.error(resolveErrorMessage(resp?.error_message, '停止失败'));
                    refresh();
                  } catch (e) {
                    message.error(e?.message || String(e));
                    refresh();
                  }
                },
              });
            }}
            disabled={!activeRunId}
            style={{ padding: '0 4px' }}
          >
            停止运行
          </Button>

          <Button
            type="link"
            size="small"
            onClick={() => {
              setHistorySchedule(row);
              setHistoryOpen(true);
            }}
            icon={<HistoryOutlined />}
            style={{ padding: '0 4px' }}
          >
            运行历史
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(row);
              setEditorOpen(true);
            }}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>

          <Button
            type="link"
            size="small"
            onClick={async () => {
              try {
                const resp = await updateProducerSchedule({ schedule_id: row.schedule_id, enabled: !row.enabled });
                if (resp?.status === 'success') {
                  message.success(!row.enabled ? '已启用' : '已停用');
                  refresh();
                } else {
                  message.error(resolveErrorMessage(resp?.error_message, '更新失败'));
                }
              } catch (e) {
                message.error(e?.message || String(e));
              }
            }}
            icon={<PoweroffOutlined />}
            style={{ padding: '0 4px' }}
          >
            {row.enabled ? '停用' : '启用'}
          </Button>

          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: '确定删除这个任务？',
                okText: '删除',
                okType: 'danger',
                cancelText: '取消',
                onOk: async () => {
                  try {
                    const resp = await deleteProducerSchedules(row.schedule_id);
                    if (resp?.status === 'success') {
                      message.success('已删除');
                      markScheduleIdsDeleted([row.schedule_id]);
                      setItems((prev) => (prev || []).filter((it) => it?.schedule_id !== row.schedule_id));
                      await new Promise((r) => setTimeout(r, 300));
                      await reloadAfterDelete([row.schedule_id]);
                    } else {
                      message.error(resolveErrorMessage(resp?.error_message, '删除失败'));
                    }
                  } catch (e) {
                    message.error(e?.message || String(e));
                  }
                },
              });
            }}
            style={{ padding: '0 4px' }}
          >
            删除
          </Button>
        </Space>
        );
      },
    },
  ]), [markScheduleIdsDeleted, reloadAfterDelete, refresh, runSummaryByScheduleId]);

  const defaultProducerName = filterProducer || 'papers_to_notes';

  return (
    <div className="page-panel">
      <div className="page-panel__top">
        <Form
          form={form}
          layout="horizontal"
          onFinish={() => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            refresh(1, paginationRef.current.pageSize);
          }}
          style={{ marginBottom: 0 }}
        >
          <Row gutter={[16, 16]} justify="space-between">
            <Col span={18}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Form.Item label="生产能力" style={{ marginBottom: 0 }}>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="按生产能力筛选"
                      allowClear
                      options={PRODUCER_OPTIONS}
                      value={filterProducer}
                      onChange={(v) => setFilterProducer(v)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="状态" style={{ marginBottom: 0 }}>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="按启用状态筛选"
                      allowClear
                      options={[
                        { label: '启用', value: true },
                        { label: '停用', value: false },
                      ]}
                      value={filterEnabled}
                      onChange={(v) => setFilterEnabled(v)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button type="primary" style={{ width: 80 }} onClick={() => form.submit()} loading={loading}>
                  检索
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditing(null);
                    setEditorOpen(true);
                  }}
                >
                  创建生产任务
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="page-panel__body">
        <Table
          size="small"
          rowKey="schedule_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          locale={{ emptyText: '暂无任务' }}
          scroll={{ x: 3200 }}
        />
      </div>

      <div
        className="page-panel__bottom"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px 0',
          background: '#fff',
        }}
      >
        <div />
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showTotal={(total) => `共 ${total} 条记录`}
          showSizeChanger
          showQuickJumper
          style={{ margin: 0 }}
          onChange={(page, pageSize) => {
            setPagination((prev) => ({ ...prev, current: page, pageSize }));
            refresh(page, pageSize);
          }}
          onShowSizeChange={(current, size) => {
            setPagination((prev) => ({ ...prev, current: 1, pageSize: size }));
            refresh(1, size);
          }}
        />
      </div>

      <ProducerScheduleEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={() => refresh()}
        initialSchedule={editing}
        defaultProducerName={defaultProducerName}
      />

      <Drawer
        title="本次运行详情"
        open={runViewerOpen}
        onClose={() => setRunViewerOpen(false)}
        width={720}
        destroyOnClose
      >
        <ProducerRunViewer
          runId={activeRunId}
          producerName={activeProducerName}
          title="手动触发运行"
        />
      </Drawer>

      <ProduceRunHistoryDrawer
        open={historyOpen}
        schedule={historySchedule}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
};

export default ProducerSchedulesPanel;
